/* eslint-disable no-unused-expressions */
import React, { useMemo, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import "../styles/UploadedFile.scss";
import CustomDatePicker from "../../CustomDatePicker/CustomDatepicker";
import NumberInput from "../../NumberInput/NumberInput";
import FieldsetSelect from "../../FieldsetSelect/FieldsetSelect";
import Switch from "../../Switch/Switch";
import SmallLoading from "../../SmallLoading/SmallLoading";
import { getService } from "../../../services/axios/services";
import AddendumSelect from "./Addendum/AddendumSelect";
import {
  ADDENDUM_TYPE_ID,
  getEpisodesWithAttachments,
  getChaptersWithAttachments,
  isAddendumType,
} from "./Addendum/addendum.utils";

const UploadedDramaturgy = (props) => {
  const {
    thesaurusFileType,
    state,
    getDate,
    getVersion,
    getMaterialVersion,
    getAttachmentType,
    getChapterAndBlock,
    getReason,
    setStructuringReview,
    setEpisodeIds,
    darkBackgroundColor = true,
    isMaterial,
    isChapter,
    hideMaterialType,
    freeVersionInput = false,
    materialsList = [],
    dramaturgyList = [],
    isAddendumEdit = false,
    initialAddendumIds = [],
  } = props;
  const mode = isMaterial ? "material" : isChapter ? "chapter" : "default";
  const location = useLocation();
  const contentApiPrefix = location.pathname.includes("variedades")
    ? "varieties"
    : "dramaturgies";

  const [reasons, setReasons] = useState([]);
  const [loadingReasons, setLoadingReasons] = useState(true);

  useEffect(() => {
    if (!state.structuringReview) return;
    setLoadingReasons(true);
    getService({
      endpoint: `${contentApiPrefix}/thesaurus`,
      params: { origins: 38 },
    })
      .then((res) => {
        setReasons(res.data || []);
      })
      .finally(() => setLoadingReasons(false));
  }, [state.structuringReview, contentApiPrefix]);

  const materialTypeOptions = (() => {
    const fromMaterials = materialsList
      .filter(
        (material, index, self) =>
          material?.type?.id !== undefined &&
          index === self.findIndex((m) => m?.type?.id === material.type.id)
      )
      .map((material) => ({
        id: material.type.id,
        name:
          isMaterial && Number(material.type.id) === 1211
            ? "Episódio"
            : material?.type.name || `ID ${material.type.id}`,
      }));

    const addendumFromThesaurus = (
      Array.isArray(thesaurusFileType) ? thesaurusFileType : []
    ).find((item) => Number(item?.id) === ADDENDUM_TYPE_ID);

    const baseOptions = [...fromMaterials];

    if (
      addendumFromThesaurus &&
      !baseOptions.some(
        (option) => Number(option.id) === Number(addendumFromThesaurus.id)
      )
    ) {
      baseOptions.push(addendumFromThesaurus);
    }

    if (isChapter && dramaturgyList?.length) {
      const has1211 = baseOptions.some((m) => Number(m.id) === 1211);
      const combined = has1211
        ? baseOptions
        : [{ id: 1211, name: "Capítulo" }, ...baseOptions];
      return combined.sort((a, b) => a.name.localeCompare(b.name));
    }

    return baseOptions.sort((a, b) => a.name.localeCompare(b.name));
  })();

  const versionOptions = useMemo(() => {
    if (!state.attachmentTypeId) return [];

    const isEpisode = Number(state.attachmentTypeId) === 1211;

    if (isChapter && isEpisode) {
      const selectedChapter = dramaturgyList.find((c) => c.id === state.id);
      const existingVersions = selectedChapter?.versions ?? [];
      const maxVersion = existingVersions.length
        ? Math.max(...existingVersions)
        : 0;
      const nextVersion = maxVersion + 1;
      return [...existingVersions, nextVersion].map((v) => ({
        value: v,
        label: v,
      }));
    }

    const filtered = materialsList.filter((item) => {
      if (
        !item?.type?.id ||
        Number(item.type.id) !== Number(state.attachmentTypeId)
      )
        return false;
      if (
        isEpisode &&
        Number(item.episodeNumber) !== Number(state.chapterOrEpisodeNumber)
      )
        return false;
      return true;
    });

    const seen = new Set();
    const options = filtered.reduce((acc, item) => {
      if (!seen.has(item.version)) {
        seen.add(item.version);
        acc.push({ value: item.version, label: item.version });
      }
      return acc;
    }, []);

    return options;
  }, [
    isChapter,
    state.attachmentTypeId,
    state.id,
    state.chapterOrEpisodeNumber,
    materialsList,
    dramaturgyList,
  ]);

  const episodeOptions = useMemo(() => {
    const seen = new Set();
    return materialsList
      .filter((item) => {
        if (!item?.type?.id || Number(item.type.id) !== 1211) return false;
        if (seen.has(item.episodeNumber)) return false;
        seen.add(item.episodeNumber);
        return true;
      })
      .map((item) => ({
        id: item.episodeNumber,
        episodeId: item.episodeId,
        episodeNumber: item.episodeNumber,
        name: item.episodeNumber,
      }));
  }, [materialsList]);

  const episodeOptionsWithAttachments = useMemo(
    () => getEpisodesWithAttachments(materialsList, episodeOptions),
    [episodeOptions, materialsList]
  );

  const dramaturgyListWithAttachments = useMemo(
    () => getChaptersWithAttachments(dramaturgyList),
    [dramaturgyList]
  );

  const hasChapterWithAttachments = useMemo(
    () => dramaturgyListWithAttachments.length > 0,
    [dramaturgyListWithAttachments]
  );

  const hasEpisodeWithAttachments = useMemo(
    () => episodeOptionsWithAttachments.length > 0,
    [episodeOptionsWithAttachments]
  );

  const allMaterialTypeOptions = useMemo(() => {
    const options = [...materialTypeOptions];

    return options
      .filter(
        (option) =>
          Number(option.id) !== ADDENDUM_TYPE_ID ||
          hasChapterWithAttachments ||
          hasEpisodeWithAttachments
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [
    materialTypeOptions,
    hasChapterWithAttachments,
    hasEpisodeWithAttachments,
  ]);

  const isAddendum = useMemo(
    () => isAddendumType(state.attachmentTypeId),
    [state.attachmentTypeId]
  );

  const newDate = state.receivedAt ? new Date(state.receivedAt) : new Date();

  const getChapterVersion = (value) => {
    const suggestedVersion = versionOptions[0]?.value ?? 1;
    getMaterialVersion({ target: { value: value ?? suggestedVersion } });
  };

  const renderMaterialFields = () => {
    return (
      <FieldsetSelect
        data={allMaterialTypeOptions}
        label="Tipo do material"
        placeholder="Selecione"
        handleChange={getAttachmentType}
        selected={state.attachmentTypeId}
        darkBackgroundColor={darkBackgroundColor}
      />
    );
  };

  const renderVersionField = () => {
    if (isAddendum || isAddendumEdit) return <></>;

    if (mode === "chapter" && Number(state.attachmentTypeId) === 1211) {
      if (freeVersionInput) {
        return (
          <NumberInput
            label="Versão do Material"
            getVersion={(value) => getMaterialVersion({ target: { value } })}
            darkBackgroundColor={darkBackgroundColor}
            number={state.version}
            maxNumber={99}
            minNumber={1}
          />
        );
      }
      const suggestedVersion = versionOptions[0]?.value ?? 1;
      return (
        <NumberInput
          label="Versão do Material"
          getVersion={getChapterVersion}
          darkBackgroundColor={darkBackgroundColor}
          number={state.version ?? suggestedVersion}
          maxNumber={99}
          minNumber={1}
        />
      );
    }

    if (
      mode === "material" ||
      (mode === "chapter" && Number(state.attachmentTypeId) !== 1211)
    ) {
      return (
        <FieldsetSelect
          data={versionOptions}
          label="Versão do Material"
          placeholder="Selecione"
          handleChange={getMaterialVersion}
          selected={state.version}
          darkBackgroundColor={darkBackgroundColor}
        />
      );
    }

    if (mode === "default" && thesaurusFileType) {
      return (
        <NumberInput
          label="Versão"
          getVersion={getVersion}
          darkBackgroundColor={darkBackgroundColor}
          number={state.version}
        />
      );
    }

    return null;
  };

  const renderMainSelectField = () => {
    if (mode === "material") {
      return (
        <>
          {!hideMaterialType && renderMaterialFields()}

          {(isAddendum || isAddendumEdit) && (
            <AddendumSelect
              dramaturgyList={dramaturgyListWithAttachments}
              episodeList={episodeOptionsWithAttachments}
              onEpisodeIdsChange={setEpisodeIds}
              initialIds={initialAddendumIds}
              darkBackgroundColor={!isAddendumEdit}
              isMaterial
            />
          )}

          {Number(state.attachmentTypeId) === 1211 && !isAddendumEdit && (
            <FieldsetSelect
              data={episodeOptions}
              label="Nº Episódio"
              placeholder="Selecione o Episódio"
              handleChange={getChapterAndBlock}
              selected={state.chapterOrEpisodeNumber}
              darkBackgroundColor={darkBackgroundColor}
            />
          )}
        </>
      );
    }

    if (isChapter) {
      return (
        <>
          {!hideMaterialType && renderMaterialFields()}

          {(isAddendum || isAddendumEdit) && (
            <AddendumSelect
              dramaturgyList={dramaturgyListWithAttachments}
              onEpisodeIdsChange={setEpisodeIds}
              initialIds={initialAddendumIds}
              darkBackgroundColor={!isAddendumEdit}
            />
          )}

          {Number(state.attachmentTypeId) === 1211 && !isAddendumEdit && (
            <FieldsetSelect
              data={dramaturgyList || []}
              label={`Nº do Capítulo`}
              placeholder={`Selecione um Capítulo`}
              name="name"
              handleChange={getChapterAndBlock}
              selected={state.id}
              darkBackgroundColor={darkBackgroundColor}
            />
          )}
        </>
      );
    }

    return (
      <FieldsetSelect
        data={dramaturgyList || []}
        label={`Nº do Capítulo`}
        placeholder={`Selecione um Capítulo`}
        name="name"
        handleChange={getChapterAndBlock}
        selected={state.id}
        darkBackgroundColor={darkBackgroundColor}
      />
    );
  };

  const showStructuringSwitch = () => {
    if (isAddendum || isAddendumEdit) return false;

    return (
      (mode === "material" && isMaterial) || (mode === "default" && !isChapter)
    );
  };

  return (
    <div className="containerFileInfo">
      <div
        className={`${mode === "material" || isChapter ? "fileInfoMaterial" : "fileInfo"} ${
          !thesaurusFileType ? "onlyDatepicker" : ""
        }`}
      >
        {renderMainSelectField()}
        {renderVersionField()}

        <CustomDatePicker
          label="Entregue em"
          name="attachmentReceivedAt"
          darkBackgroundColor={darkBackgroundColor}
          handleChangeDate={getDate}
          maxDate={new Date()}
          date={(e) => (!!e ? e : newDate)}
          placement="bottom-end"
        />
      </div>

      <div className="switch">
        {showStructuringSwitch() && (
          <Switch
            switchOnText="Revisão Estruturante"
            replaceYesAndNo={true}
            event={() => setStructuringReview(!state.structuringReview)}
            switchOn={!!state.structuringReview}
          />
        )}
      </div>

      {state.structuringReview && (
        <div
          className={`fileInfo ${!thesaurusFileType ? "onlyDatepicker" : ""}`}
        >
          {loadingReasons ? (
            <SmallLoading />
          ) : (
            <FieldsetSelect
              data={reasons}
              label="Motivo"
              placeholder="Selecione um Motivo"
              name="name"
              handleChange={getReason}
              selected={state.reason}
              darkBackgroundColor={darkBackgroundColor}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(UploadedDramaturgy);
